/**
 * Olfacy header: drawer (Categories / Brands tabs, sub-lists, brand filter) and predictive search
 * (Shopify's /search/suggest.json — no app). Tiny, deferred, event-delegated.
 */
class OlfacyHeader extends HTMLElement {
  connectedCallback() {
    this.abort = new AbortController();
    const { signal } = this.abort;
    this.drawer = this.querySelector('[data-drawer]');

    this.addEventListener('click', (e) => {
      if (e.target.closest('[data-open-drawer]')) this.drawer?.showModal();
      if (e.target.closest('[data-close-drawer]')) this.drawer?.close();
      const tab = e.target.closest('[data-drawer-tab]');
      if (tab) this.showTab(tab.dataset.drawerTab);
      const openSub = e.target.closest('[data-open-sub]');
      if (openSub) this.showSub(openSub.dataset.openSub);
      if (e.target.closest('[data-close-sub]')) this.showSub(null);
    }, { signal });

    this.drawer?.addEventListener('click', (e) => { if (e.target === this.drawer) this.drawer.close(); }, { signal });

    this.querySelector('[data-brand-filter]')?.addEventListener('input', (e) => this.filterBrands(e.target.value), { signal });

    this.setupSearch(signal);
    this.setupMega(signal);
    this.setupHideOnScroll(signal);
  }

  /* ---------- Brands mega menu ---------- */
  setupMega(signal) {
    this.mega = this.querySelector('[data-mega]');
    this.megaToggle = this.querySelector('[data-mega-toggle]');
    this.megaBackdrop = this.querySelector('[data-mega-backdrop]');
    if (!this.mega || !this.megaToggle) return;
    this.megaItems = [];
    this.topLabel = this.mega.querySelector('[data-mega-heading]')?.textContent || 'Top Brands';

    this.megaToggle.addEventListener('click', async (e) => {
      if (!window.matchMedia('(min-width: 990px)').matches) return; // mobile: normal link
      e.preventDefault();
      const opening = this.mega.hidden;
      this.toggleMega(opening);
      if (opening) await this.fillMegaList();
    }, { signal });
    this.megaBackdrop?.addEventListener('click', () => this.toggleMega(false), { signal });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && !this.mega.hidden) { this.toggleMega(false); this.megaToggle.focus(); } }, { signal });

    this.mega.addEventListener('click', (e) => {
      const tab = e.target.closest('[data-mega-tab]');
      if (tab) {
        this.mega.querySelectorAll('[data-mega-tab]').forEach((t) => t.setAttribute('aria-selected', String(t === tab)));
        this.mega.querySelectorAll('[data-mega-panel]').forEach((p) => (p.hidden = p.dataset.megaPanel !== tab.dataset.megaTab));
      }
      const letter = e.target.closest('[data-letter]');
      if (letter && !letter.disabled) this.filterMega({ letter: letter.dataset.letter });
    }, { signal });
    this.mega.querySelector('[data-mega-search]')?.addEventListener('input', (e) => this.filterMega({ query: e.target.value }), { signal });

  }

  /** Fetch the brand index once (only when Brands is first opened) */
  loadBrands() {
    if (!this.brandsPromise) {
      const root = (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
      this.brandsPromise = fetch(`${root}?section_id=olfacy-brands-index`)
        .then((r) => r.text())
        .then((html) => [...new DOMParser().parseFromString(html, 'text/html').querySelectorAll('[data-brands-index] > li')])
        .catch(() => []);
    }
    return this.brandsPromise;
  }

  async fillMegaList() {
    if (this.megaFilled) return;
    this.megaFilled = true;
    const list = this.mega.querySelector('[data-mega-list]');
    const top = list.dataset.topHandles || ',';
    const noTop = top === ',';
    const items = await this.loadBrands();
    const fragment = document.createDocumentFragment();
    for (const src of items) {
      const li = document.createElement('li');
      li.dataset.name = src.dataset.name;
      if (noTop || top.includes(`,${src.dataset.handle},`)) li.dataset.top = '';
      const a = document.createElement('a');
      a.href = src.querySelector('a')?.getAttribute('href') || '#';
      a.textContent = src.querySelector('.ohd__brand-name')?.textContent || '';
      li.append(a);
      fragment.append(li);
    }
    list.append(fragment);
    this.megaItems = [...list.children];
    // Disable letters that have no brands
    const firsts = new Set(this.megaItems.map((li) => (/^[0-9]/.test(li.dataset.name) ? '#' : li.dataset.name.charAt(0).toUpperCase())));
    this.mega.querySelectorAll('[data-letter]').forEach((b) => { if (b.dataset.letter !== '*') b.disabled = !firsts.has(b.dataset.letter); });
    this.filterMega({ letter: '*' });
  }

  async fillDrawerBrands() {
    if (this.drawerFilled) return;
    this.drawerFilled = true;
    const list = this.querySelector('[data-brand-list]');
    if (!list) return;
    const items = await this.loadBrands();
    list.append(...items.map((li) => document.importNode(li, true)));
    const empty = this.querySelector('[data-brand-empty]');
    if (empty) empty.hidden = items.length > 0;
  }

  toggleMega(open) {
    this.mega.hidden = !open;
    if (this.megaBackdrop) this.megaBackdrop.hidden = !open;
    this.megaToggle.setAttribute('aria-expanded', String(open));
    if (open) this.mega.querySelector('[data-mega-search]')?.focus({ preventScroll: true });
  }

  filterMega({ letter = null, query = null }) {
    const heading = this.mega.querySelector('[data-mega-heading]');
    const q = (query || '').trim().toLowerCase();
    let shown = 0;
    this.megaItems.forEach((li) => {
      const name = li.dataset.name;
      let match;
      if (query !== null && q) match = name.includes(q);
      else if (letter === '#') match = /^[0-9]/.test(name);
      else if (letter && letter !== '*') match = name.charAt(0).toUpperCase() === letter;
      else match = li.hasAttribute('data-top');
      li.hidden = !match;
      if (match) shown++;
    });
    if (query === null || !q) {
      const active = letter ?? '*';
      this.mega.querySelectorAll('[data-letter]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.letter === active)));
      if (heading) heading.textContent = active === '*' ? this.topLabel : active;
    } else if (heading) heading.textContent = 'Results';
    const empty = this.mega.querySelector('[data-mega-empty]');
    if (empty) empty.hidden = shown > 0;
  }

  /* ---------- Hide on scroll down, show on scroll up ---------- */
  setupHideOnScroll(signal) {
    if (!this.hasAttribute('data-hide-on-scroll')) return;
    const group = this.closest('#header-group') || document.getElementById('header-group');
    if (!group) return;
    let last = window.scrollY;
    let ticking = false;
    window.addEventListener('scroll', () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        const y = window.scrollY;
        const busy = (this.mega && !this.mega.hidden) || this.drawer?.open || (this.box && !this.box.hidden);
        if (busy || y < group.offsetHeight) group.classList.remove('ohd-is-hidden');
        else if (y > last + 4) group.classList.add('ohd-is-hidden');
        else if (y < last - 4) group.classList.remove('ohd-is-hidden');
        last = y;
      });
    }, { passive: true, signal });
  }

  disconnectedCallback() {
    this.abort?.abort();
  }

  /* ---------- Drawer ---------- */
  showTab(name) {
    this.querySelectorAll('[data-drawer-tab]').forEach((b) => b.setAttribute('aria-selected', String(b.dataset.drawerTab === name)));
    this.querySelectorAll('[data-drawer-panel]').forEach((p) => (p.hidden = p.dataset.drawerPanel !== name));
    if (name === 'brands') this.fillDrawerBrands();
  }

  showSub(id) {
    const root = this.querySelector('[data-cat-root]');
    this.querySelectorAll('.ohd__sub').forEach((s) => (s.hidden = s.id !== id));
    if (root) root.hidden = Boolean(id);
  }

  filterBrands(query) {
    const q = query.trim().toLowerCase();
    let shown = 0;
    this.querySelectorAll('[data-brand-list] > li').forEach((li) => {
      const match = !q || li.dataset.name.includes(q);
      li.hidden = !match;
      if (match) shown++;
    });
    const empty = this.querySelector('[data-brand-empty]');
    if (empty) empty.hidden = shown > 0;
  }

  /* ---------- Predictive search ---------- */
  setupSearch(signal) {
    this.input = this.querySelector('[data-search-input]');
    this.box = this.querySelector('[data-suggest]');
    if (!this.input || !this.box) return;

    const mobile = window.matchMedia('(max-width: 989px)');
    const setPlaceholder = () => {
      if (!this.defaultPlaceholder) this.defaultPlaceholder = this.input.placeholder;
      this.input.placeholder = mobile.matches ? this.input.dataset.placeholderMobile || this.defaultPlaceholder : this.defaultPlaceholder;
    };
    setPlaceholder();
    mobile.addEventListener('change', setPlaceholder, { signal });

    let timer;
    this.input.addEventListener('input', () => {
      clearTimeout(timer);
      timer = setTimeout(() => this.suggest(this.input.value.trim()), 180);
    }, { signal });
    this.input.addEventListener('keydown', (e) => this.onKey(e), { signal });
    this.input.addEventListener('focus', () => { if (this.box.innerHTML && this.input.value.trim()) this.open(true); }, { signal });
    document.addEventListener('click', (e) => { if (!this.querySelector('[data-search]').contains(e.target)) this.open(false); }, { signal });
  }

  open(state) {
    this.box.hidden = !state;
    this.input.setAttribute('aria-expanded', String(state));
  }

  async suggest(q) {
    if (q.length < 2) return this.open(false);
    this.controller?.abort();
    this.controller = new AbortController();
    const url = new URL(this.dataset.suggestUrl, location.origin);
    url.searchParams.set('q', q);
    url.searchParams.set('resources[type]', 'product,collection,query');
    url.searchParams.set('resources[limit]', '6');
    url.searchParams.set('resources[options][unavailable_products]', 'last');
    try {
      const res = await fetch(url, { signal: this.controller.signal });
      const data = await res.json();
      this.render(q, data.resources?.results || {});
    } catch (e) {
      if (e.name !== 'AbortError') this.open(false);
    }
  }

  render(q, results) {
    const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
    const parts = [];
    const products = results.products || [];
    const collections = results.collections || [];
    if (products.length) {
      parts.push('<p class="ohd__suggest-title">Products</p>');
      for (const p of products) {
        const img = p.featured_image?.url || p.image;
        const src = img ? `${img}${img.includes('?') ? '&' : '?'}width=80` : '';
        parts.push(`<a role="option" href="${esc(p.url)}">${src ? `<img src="${esc(src)}" alt="" width="40" height="40" loading="lazy">` : ''}<span>${esc(p.title)}</span><span class="ohd__suggest-price">${esc(p.price ? this.money(p.price) : '')}</span></a>`);
      }
    }
    if (collections.length) {
      parts.push('<p class="ohd__suggest-title">Brands &amp; collections</p>');
      for (const c of collections) parts.push(`<a role="option" href="${esc(c.url)}"><span>${esc(c.title)}</span></a>`);
    }
    const searchUrl = `${this.dataset.searchUrl}?q=${encodeURIComponent(q)}&options[prefix]=last`;
    parts.push(`<a role="option" class="ohd__suggest-all" href="${esc(searchUrl)}">Search for “${esc(q)}”</a>`);
    this.box.innerHTML = parts.join('');
    this.active = -1;
    this.open(true);
  }

  money(price) {
    // suggest.json returns price as a decimal string in the shop currency, e.g. "12000.00"
    const n = Number(price);
    if (Number.isNaN(n)) return price;
    const fmt = new Intl.NumberFormat('en-IN', { minimumFractionDigits: n % 1 ? 2 : 0, maximumFractionDigits: 2 }).format(n);
    return `₹${fmt}`;
  }

  onKey(e) {
    const options = [...this.box.querySelectorAll('[role="option"]')];
    if (e.key === 'Escape') return this.open(false);
    if (!options.length || this.box.hidden) return;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      this.active = (this.active + (e.key === 'ArrowDown' ? 1 : -1) + options.length) % options.length;
      options.forEach((o, i) => o.setAttribute('aria-selected', String(i === this.active)));
      options[this.active].scrollIntoView({ block: 'nearest' });
    } else if (e.key === 'Enter' && this.active >= 0) {
      e.preventDefault();
      location.href = options[this.active].href;
    }
  }
}

if (!customElements.get('olfacy-header')) {
  customElements.define('olfacy-header', OlfacyHeader);
}
